import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AdminPermissions, Applicant, User, UserRole, UserStatus } from '../types';
import Modal from '../components/Modal';
import AdminPermissionsEditor from '../components/AdminPermissionsEditor';
import { hasAdminPermission } from '../utils/adminPermissions';
import ApplicantProfileView from '../components/ApplicantProfileView';
import Avatar from '../components/Avatar';
import { yearLevelCategoryOf, YEAR_LEVEL_CATEGORY_OPTIONS } from '../constants/yearLevelCategory';

// An Admin or Super Admin can promote/demote between these two roles
// only — Super Admin itself is deliberately excluded from this UI
// entirely (for every viewer, including a genuine Super Admin) so there
// is no self-service path to the highest privilege level. The server
// enforces this independently — see targetVisibleToCaller in routes.ts.
const ASSIGNABLE_ROLES = [UserRole.APPLICANT, UserRole.ADMIN, UserRole.VIEWER];

const ROLE_LABEL: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.VIEWER]: 'Viewer',
  [UserRole.APPLICANT]: 'Student',
  [UserRole.GUEST]: 'Guest'
};

const STATUS_BADGE: Record<string, string> = {
  active: 'badge-success',
  inactive: 'badge-secondary',
  suspended: 'badge-warning',
  deactivated: 'badge-error'
};

function formatDate(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const isViewer = currentUser?.role === UserRole.VIEWER;
  // What the Super Admin has left switched on for this Admin. Always
  // true for a Super Admin; a Viewer is held back by isViewer instead.
  const canResetPassword = hasAdminPermission(currentUser, 'resetPassword');
  const canDisableAccount = hasAdminPermission(currentUser, 'disableAccount');
  const canDeleteAccount = hasAdminPermission(currentUser, 'deleteAccount');
  const canAssignRoles = hasAdminPermission(currentUser, 'assignRoles');
  const canRegisterUser = hasAdminPermission(currentUser, 'registerUser');
  // Without Assign Roles, registering a staff account would be assigning
  // a role by the back door — the server refuses it, so don't offer it.
  const creatableRoles = canAssignRoles ? ASSIGNABLE_ROLES : [UserRole.APPLICANT];
  const [editingPermissionsUser, setEditingPermissionsUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  // userId -> why it was flagged. Empty until the scan returns, and
  // left empty if it fails; the filter below simply finds nothing then.
  const [duplicates, setDuplicates] = useState<Map<number, string[]>>(new Map());
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [resettingUser, setResettingUser] = useState<User | null>(null);
  const [resetting, setResetting] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: UserRole.APPLICANT
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [viewingProfile, setViewingProfile] = useState<Applicant | null>(null);
  const [viewingProfileLoading, setViewingProfileLoading] = useState(false);
  const [viewingProfileError, setViewingProfileError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    apiService
      .getUsers()
      .then(setUsers)
      .catch((err) => setError(err.message || 'Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // Loaded alongside the user list, not with it. The scan is advisory,
  // so a failure here leaves the page fully usable minus the flag —
  // far better than an empty User Management because one extra query
  // fell over.
  useEffect(() => {
    apiService
      .getDuplicateUsers()
      // Typed as a tuple, or TypeScript widens the pair to
      // (number | string[])[] and the Map constructor rejects it.
      .then((flags) => setDuplicates(new Map(flags.map((f): [number, string[]] => [f.userId, f.reasons]))))
      .catch((err) => console.error('[Users] Could not load duplicate flags', err));
  }, []);

  const handleRoleChange = async (targetUser: User, role: UserRole) => {
    setBusyUserId(targetUser.id);
    setError('');
    try {
      const updated = await apiService.updateUserAccount(targetUser.id, { role });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleToggleStatus = async (targetUser: User) => {
    setBusyUserId(targetUser.id);
    setError('');
    try {
      const nextStatus = targetUser.status === UserStatus.ACTIVE ? UserStatus.DEACTIVATED : UserStatus.ACTIVE;
      const updated = await apiService.updateUserAccount(targetUser.id, { status: nextStatus });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err: any) {
      setError(err.message || 'Failed to update account status');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleConfirmReset = async () => {
    if (!resettingUser) return;
    setResetting(true);
    setError('');
    try {
      const result = await apiService.resetUserPassword(resettingUser.id);
      setTemporaryPassword(result.temporaryPassword);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const closeResetModal = () => {
    setResettingUser(null);
    setTemporaryPassword('');
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    setCreateForm({ firstName: '', lastName: '', email: '', password: '', role: creatableRoles.includes(UserRole.ADMIN) ? UserRole.ADMIN : UserRole.APPLICANT });
    setCreateError('');
  };

  const handleSavePermissions = async (targetUser: User, permissions: AdminPermissions) => {
    const updated = await apiService.updateAdminPermissions(targetUser.id, permissions);
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    setEditingPermissionsUser(null);
    setNotice(`Permissions updated for ${updated.firstName} ${updated.lastName}. They apply from their next action.`);
  };

  const handleToggleReviewer = async (targetUser: User) => {
    setBusyUserId(targetUser.id);
    setError('');
    try {
      const updated = await apiService.updateUserAccount(targetUser.id, { isDeletionReviewer: !targetUser.isDeletionReviewer });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err: any) {
      setError(err.message || 'Failed to update deletion reviewer status');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    setError('');
    setNotice('');
    try {
      const result = await apiService.deleteUser(deletingUser.id);
      setDeletingUser(null);
      if (result.status === 'pending') setNotice(result.message);
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const openProfile = async (u: User) => {
    setViewingUser(u);
    setViewingProfile(null);
    setViewingProfileError('');
    setViewingProfileLoading(true);
    try {
      const profile = await apiService.getApplicantProfileByUserId(u.id);
      setViewingProfile(profile);
    } catch (err: any) {
      // Most commonly a 404 — this user (e.g. one who registered but never
      // filled out their profile yet) has no Applicant record at all.
      setViewingProfileError(err.message || 'This user has no profile to view yet.');
    } finally {
      setViewingProfileLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      await apiService.createUser(createForm);
      closeCreateForm();
      load();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const filtered = users.filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (statusFilter && u.status !== statusFilter) return false;
    if (categoryFilter && yearLevelCategoryOf(u.yearLevel) !== categoryFilter) return false;
    if (duplicatesOnly && !duplicates.has(u.id)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = `${u.firstName} ${u.lastName}`.toLowerCase().includes(q);
      const matchesEmail = u.email.toLowerCase().includes(q);
      if (!matchesName && !matchesEmail) return false;
    }
    return true;
  });

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Users</div>
        <div className="navbar-actions">
          {/* Bulk "Delete All" has been disabled system-wide. The server
              refuses DELETE /users regardless; the button is removed so
              it is not offered. Single-account deletion, per row, remains. */}
          {!isViewer && canRegisterUser && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (!creatableRoles.includes(createForm.role)) setCreateForm({ ...createForm, role: UserRole.APPLICANT });
                setShowCreateForm(true);
              }}
            >
              Register New User
            </button>
          )}
        </div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>User Management</h1>
          <p>Assign roles, enable/disable accounts, and reset passwords for any user.</p>
        </div>

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {notice && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(167, 139, 250, 0.12)',
              border: '1px solid rgba(167, 139, 250, 0.35)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}
          >
            {notice}
          </div>
        )}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, minWidth: '220px', flex: 1 }}>
              <label htmlFor="userSearch">Search</label>
              <input id="userSearch" placeholder="Name or email" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '160px', flex: 1 }}>
              <label htmlFor="userRoleFilter">Role</label>
              <select id="userRoleFilter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">All roles</option>
                {Object.values(UserRole)
                  .filter((r) => r !== UserRole.GUEST)
                  .filter((r) => r !== UserRole.SUPER_ADMIN || currentUser?.role === UserRole.SUPER_ADMIN)
                  .map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABEL[role]}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '160px', flex: 1 }}>
              <label htmlFor="userStatusFilter">Status</label>
              <select id="userStatusFilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {Object.values(UserStatus).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
              <label htmlFor="userCategoryFilter">Filter by Group</label>
              <select id="userCategoryFilter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                {YEAR_LEVEL_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* A toggle rather than another dropdown: it crosses the
                other filters instead of replacing one, so "disabled
                students who look like duplicates" is a question this
                page can now answer. */}
            <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
              <label htmlFor="userDuplicateFilter">Duplicate/Double Users</label>
              <label
                htmlFor="userDuplicateFilter"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem 0' }}
              >
                <input
                  id="userDuplicateFilter"
                  type="checkbox"
                  checked={duplicatesOnly}
                  onChange={(e) => setDuplicatesOnly(e.target.checked)}
                />
                <span style={{ fontSize: '0.9rem' }}>
                  Show only possible duplicates
                  {duplicates.size > 0 && (
                    <span style={{ color: 'var(--text-secondary)' }}> ({duplicates.size})</span>
                  )}
                </span>
              </label>
            </div>
          </div>
        </div>

        {duplicatesOnly && (
          // Said plainly, because acting on this list without reading it
          // would be wrong: a shared handset is ordinary here, and the
          // system deliberately does not enforce uniqueness on any of
          // these fields.
          <div className="alert-error" style={{ padding: '0.85rem 1rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
            These accounts share an ID number, a phone number, or a name and date of birth with another account.
            That is a prompt to look, not a finding — households often share one phone, and siblings can share a
            surname and a birthday. Open both profiles before acting.
          </div>
        )}

        {!loading && (
          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{filtered.length.toLocaleString()}</strong>{' '}
            user{filtered.length === 1 ? '' : 's'}
            {filtered.length !== users.length ? ` (of ${users.length.toLocaleString()} total)` : ''}
          </p>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="card">
            <p style={{ color: 'var(--text-secondary)' }}>No users match this filter.</p>
          </div>
        ) : (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  {/* The evidence side of the consent tick box on the
                      registration form. Shown here because a consent
                      record nothing can display is not much use for
                      demonstrating consent. */}
                  <th>Privacy Consent</th>
                  {isSuperAdmin && <th>Deletion Reviewer</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, index) => {
                  const isSelf = currentUser?.id === u.id;
                  const isSuperAdminRow = u.role === UserRole.SUPER_ADMIN;
                  const busy = busyUserId === u.id;
                  return (
                    <tr key={u.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          <Avatar
                            userId={u.id}
                            hasPicture={Boolean(u.profilePictureUrl)}
                            name={`${u.firstName} ${u.lastName}`}
                            size={28}
                          />
                          <span>
                            {u.firstName} {u.lastName}
                            {isSelf && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}> (you)</span>}
                            {/* Marked on every view, not only when the
                                filter is on — otherwise the flag is
                                invisible to anyone who never thinks to
                                tick it. The reasons go in the tooltip;
                                the row only needs to say "look". */}
                            {duplicates.has(u.id) && (
                              <span
                                className="badge badge-secondary"
                                title={`Possible duplicate — ${duplicates.get(u.id)!.join('; ')}`}
                                style={{ marginLeft: '0.4rem', fontSize: '0.7rem' }}
                              >
                                Possible duplicate
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {isViewer || isSelf || isSuperAdminRow || !canAssignRoles ? (
                          <span className="badge badge-secondary">{ROLE_LABEL[u.role]}</span>
                        ) : (
                          <select
                            value={u.role}
                            disabled={busy}
                            onChange={(e) => handleRoleChange(u, e.target.value as UserRole)}
                            style={{ minWidth: '120px' }}
                          >
                            {ASSIGNABLE_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_LABEL[role]}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[u.status] ?? 'badge-secondary'}`}>{u.status}</span>
                        {/* Still on a password a staff member chose —
                            reset or newly created, not yet signed in to
                            replace it. */}
                        {u.mustChangePassword && (
                          <span
                            className="badge badge-warning"
                            title="Still using a temporary password. They'll be asked to choose their own at their next sign-in."
                            style={{ marginLeft: '0.35rem', fontSize: '0.7rem' }}
                          >
                            Temporary password
                          </span>
                        )}
                      </td>
                      <td>{formatDate(u.createdAt)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {u.privacyConsentAt ? (
                          <span
                            // The version is the hash of the exact policy
                            // text agreed to. Shown truncated on hover
                            // rather than in the column, which only needs
                            // to answer "did they, and when".
                            title={
                              u.privacyConsentVersion
                                ? `Policy version ${u.privacyConsentVersion.slice(0, 12)}`
                                : undefined
                            }
                          >
                            {formatDate(u.privacyConsentAt)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }} title="This account was created before consent was recorded.">
                            Not recorded
                          </span>
                        )}
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {u.role === UserRole.ADMIN ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                              <input
                                type="checkbox"
                                checked={u.isDeletionReviewer}
                                disabled={busy}
                                onChange={() => handleToggleReviewer(u)}
                              />
                              Reviewer
                            </label>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {u.role === UserRole.APPLICANT && (
                            <button className="btn btn-outline btn-sm" onClick={() => openProfile(u)}>
                              View Profile
                            </button>
                          )}
                          {isSuperAdmin && u.role === UserRole.ADMIN && (
                            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setEditingPermissionsUser(u)}>
                              Permissions
                            </button>
                          )}
                          {!isViewer && !isSelf && !isSuperAdminRow && (
                            <>
                              {canDisableAccount && (
                                <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => handleToggleStatus(u)}>
                                  {u.status === UserStatus.ACTIVE ? 'Disable' : 'Enable'}
                                </button>
                              )}
                              {canResetPassword && (
                                <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setResettingUser(u)}>
                                  Reset Password
                                </button>
                              )}
                            </>
                          )}
                          {!isViewer && !isSelf && !isSuperAdminRow && canDeleteAccount && u.role === UserRole.APPLICANT && (
                              <button
                                className="btn btn-outline btn-sm"
                                style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                                disabled={busy}
                                onClick={() => setDeletingUser(u)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resettingUser && (
        <Modal title="Reset Password" onClose={closeResetModal}>
          {temporaryPassword ? (
            <>
              <p style={{ color: '#34D399' }}>
                Password reset for <strong>{resettingUser.email}</strong>. Share this temporary password with them now —
                it will not be shown again.
              </p>
              <div
                style={{
                  padding: '0.85rem',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'monospace',
                  fontSize: '1.1rem',
                  textAlign: 'center'
                }}
              >
                {temporaryPassword}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-primary" type="button" onClick={closeResetModal}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="alert-error" style={{ padding: '0.85rem' }}>
                Reset <strong>{resettingUser.email}</strong>'s password? Their current password will stop working
                immediately.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn btn-primary" type="button" disabled={resetting} onClick={handleConfirmReset}>
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </button>
                <button className="btn btn-outline" type="button" onClick={closeResetModal} disabled={resetting}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {showCreateForm && (
        <Modal title="Register New User" onClose={closeCreateForm}>
          <form onSubmit={handleCreateUser}>
            {createError && (
              <p className="alert-error" style={{ padding: '0.75rem', marginBottom: '1rem' }}>
                {createError}
              </p>
            )}
            <div className="form-group">
              <label htmlFor="createFirstName">First Name</label>
              <input
                id="createFirstName"
                value={createForm.firstName}
                onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="createLastName">Last Name</label>
              <input
                id="createLastName"
                value={createForm.lastName}
                onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="createEmail">Email</label>
              <input
                id="createEmail"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="createPassword">Password</label>
              <input
                id="createPassword"
                type="text"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                minLength={8}
                required
              />
              <small style={{ color: 'var(--text-secondary)' }}>At least 8 characters. Share this with them directly.</small>
            </div>
            <div className="form-group">
              <label htmlFor="createRole">Role</label>
              <select
                id="createRole"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as UserRole })}
              >
                {creatableRoles.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABEL[role]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button className="btn btn-primary" type="submit" disabled={creating}>
                {creating ? 'Creating...' : 'Create Account'}
              </button>
              <button className="btn btn-outline" type="button" onClick={closeCreateForm} disabled={creating}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {viewingUser && (
        <Modal
          title={`Profile: ${viewingUser.firstName} ${viewingUser.lastName}`}
          onClose={() => setViewingUser(null)}
          scrollable={false}
        >
          {viewingProfileError ? (
            <p style={{ color: 'var(--text-secondary)' }}>{viewingProfileError}</p>
          ) : (
            <ApplicantProfileView
              profile={viewingProfile}
              loading={viewingProfileLoading}
              email={viewingUser.email}
              userId={viewingUser.id}
              canManageLock={!isViewer}
            />
          )}
        </Modal>
      )}

      {editingPermissionsUser && (
        <Modal
          title={`Permissions: ${editingPermissionsUser.firstName} ${editingPermissionsUser.lastName}`}
          onClose={() => setEditingPermissionsUser(null)}
        >
          <AdminPermissionsEditor
            initial={editingPermissionsUser.adminPermissions}
            onSave={(permissions) => handleSavePermissions(editingPermissionsUser, permissions)}
            onCancel={() => setEditingPermissionsUser(null)}
          />
        </Modal>
      )}

      {deletingUser && (
        <Modal title="Delete User" onClose={() => setDeletingUser(null)}>
          <p className="alert-error" style={{ padding: '0.85rem' }}>
            Delete <strong>{deletingUser.firstName} {deletingUser.lastName}</strong> ({deletingUser.email})? This permanently
            removes their account, applicant profile, applications, and uploaded documents. This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              className="btn btn-primary"
              type="button"
              style={{ background: 'var(--error-text)' }}
              disabled={deleting}
              onClick={handleConfirmDeleteUser}
            >
              {deleting ? 'Deleting...' : 'Delete User'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setDeletingUser(null)} disabled={deleting}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default UserManagementPage;
